import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { formatNumber } from '@/lib/utils'
import {
  UsersIcon,
  UserPlusIcon,
  StarIcon,
  CursorArrowRaysIcon,
} from '@heroicons/react/24/outline'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'

interface StatsData {
  totalUsers: number
  newUsersToday: number
  newUsersWeek: number
  newUsersMonth: number
  premiumUsers: number
  premiumPercent: string
  activeToday: number
  brokerClicks: number
  brokerRegistrations: number
}

interface FunnelItem {
  stage: string
  count: number
  percent: string | number
}

interface ChartData {
  registrations: { _id: string; count: number }[]
  activity: { _id: string; count: number }[]
}

export default function DashboardPage() {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const res = await api.get<{ data: StatsData }>('/admin/dashboard/stats')
      return res.data.data
    },
  })
  
  const { data: funnel } = useQuery({
    queryKey: ['dashboard-funnel'],
    queryFn: async () => {
      const res = await api.get<{ data: { funnel: FunnelItem[] } }>('/admin/dashboard/funnel')
      return res.data.data.funnel
    },
  })
  
  const { data: charts } = useQuery({
    queryKey: ['dashboard-charts'],
    queryFn: async () => {
      const res = await api.get<{ data: ChartData }>('/admin/dashboard/charts?days=30')
      return res.data.data
    },
  })
  
  const statCards = [
    {
      name: 'Всего пользователей',
      value: stats?.totalUsers || 0,
      icon: UsersIcon,
      color: 'bg-blue-500',
    },
    {
      name: 'Новых сегодня',
      value: stats?.newUsersToday || 0,
      icon: UserPlusIcon,
      color: 'bg-green-500',
    },
    {
      name: 'Premium',
      value: `${stats?.premiumUsers || 0} (${stats?.premiumPercent || 0}%)`,
      icon: StarIcon,
      color: 'bg-yellow-500',
    },
    {
      name: 'Клики брокер',
      value: stats?.brokerClicks || 0,
      icon: CursorArrowRaysIcon,
      color: 'bg-purple-500',
    },
  ]
  
  return (
    <div className="space-y-6 animate-fadeIn">
      <h1 className="text-2xl font-bold text-white">Дашборд</h1>
      
      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <div key={stat.name} className="card">
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${stat.color}`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-400">{stat.name}</p>
                <p className="text-2xl font-bold text-white">
                  {statsLoading ? '...' : formatNumber(typeof stat.value === 'number' ? stat.value : 0) || stat.value}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Registrations chart */}
        <div className="card">
          <h3 className="text-lg font-medium text-white mb-4">Регистрации за 30 дней</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={charts?.registrations || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="_id" 
                  stroke="#9ca3af"
                  tick={{ fill: '#9ca3af', fontSize: 12 }}
                  tickFormatter={(value) => value.slice(5)}
                />
                <YAxis stroke="#9ca3af" tick={{ fill: '#9ca3af', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1f2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                  }}
                  labelStyle={{ color: '#fff' }}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#0ea5e9"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        {/* Funnel */}
        <div className="card">
          <h3 className="text-lg font-medium text-white mb-4">Воронка конверсии</h3>
          <div className="space-y-3">
            {funnel?.map((item) => (
              <div key={item.stage}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-400">{item.stage}</span>
                  <span className="text-white">
                    {formatNumber(item.count)} ({item.percent}%)
                  </span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-primary-500 to-accent-500 rounded-full transition-all duration-500"
                    style={{ width: `${item.percent}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Additional stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <h4 className="text-sm text-gray-400 mb-1">Новых за неделю</h4>
          <p className="text-2xl font-bold text-white">
            {statsLoading ? '...' : formatNumber(stats?.newUsersWeek || 0)}
          </p>
        </div>
        <div className="card">
          <h4 className="text-sm text-gray-400 mb-1">Новых за месяц</h4>
          <p className="text-2xl font-bold text-white">
            {statsLoading ? '...' : formatNumber(stats?.newUsersMonth || 0)}
          </p>
        </div>
        <div className="card">
          <h4 className="text-sm text-gray-400 mb-1">Активных сегодня</h4>
          <p className="text-2xl font-bold text-white">
            {statsLoading ? '...' : formatNumber(stats?.activeToday || 0)}
          </p>
        </div>
      </div>
    </div>
  )
}
